import { divideArrayIntoChunks, hexToBlob } from '@pkg/basic'
import type { LowerHex } from '@pkg/evm'
import { and, eq, inArray, sql } from 'drizzle-orm'
import { sqliteMaxHostParameterCount } from '../../constants'
import {
  accountEvmTxIndexRelationTable,
  accountTable,
  actionSourceTable,
  actionTable,
  actionTransferTable,
  erc20TokenTable,
} from '../../schema'
import type { DatabaseWithTransaction } from '../../types'

type ResetEvmTxAnalyzedStatusByErc20TokenParameters = {
  readonly database: DatabaseWithTransaction
  readonly chainId: number
  readonly tokenAddress: LowerHex
}

/**
 * 指定した ERC20 トークンの送受金を action に含む EVM 取引を、再解析対象 (analyzed = false) に戻す。
 * - トークンの通貨 ID は erc20Token テーブルの currencyId と、未登録トークンの既定 ID (`evm_${chainId}_${address}`) の両方を対象にする
 * - 対象は指定チェーンの EVM action source に限る
 * - lock 済みの取引は同期処理側で再解析から除外される
 *
 * @param params - 対象 database、chain id、トークンアドレス (小文字)
 * @returns 再解析対象に戻した取引 (tx hash) の件数
 */
export const resetEvmTxAnalyzedStatusByErc20Token = async ({
  database: { database, transaction },
  chainId,
  tokenAddress,
}: ResetEvmTxAnalyzedStatusByErc20TokenParameters): Promise<number> => {
  // トークンに対応する通貨 ID を取得
  const cryptoIds = new Set<string>([`evm_${chainId}_${tokenAddress}`])
  const tokenRows = await database
    .select({ currencyId: erc20TokenTable.currencyId })
    .from(erc20TokenTable)
    .where(
      and(
        eq(erc20TokenTable.chainId, chainId),
        eq(erc20TokenTable.address, hexToBlob(tokenAddress)),
      ),
    )
  for (const { currencyId } of tokenRows) {
    if (currencyId !== null) {
      cryptoIds.add(currencyId)
    }
  }

  // 通貨 ID を送受金に含む action の tx hash を取得
  const sourceRows = await database
    .selectDistinct({ hash: actionSourceTable.evmHash })
    .from(actionTransferTable)
    .innerJoin(actionTable, eq(actionTransferTable.actionId, actionTable.id))
    .innerJoin(
      actionSourceTable,
      eq(actionTable.sourceId, actionSourceTable.id),
    )
    .where(
      and(
        eq(actionSourceTable.type, 'evm'),
        eq(actionSourceTable.evmChainId, chainId),
        inArray(actionTransferTable.cryptoId, [...cryptoIds]),
      ),
    )
  const hashes: Uint8Array[] = []
  for (const { hash } of sourceRows) {
    if (hash !== null) {
      hashes.push(hash)
    }
  }
  if (hashes.length === 0) {
    return 0
  }

  const evmAccountRows = await database
    .select({ id: accountTable.id })
    .from(accountTable)
    .where(eq(accountTable.type, 'evm'))

  await transaction([
    (tx) =>
      // tx hash に一致する accountEvmTxIndexRelation の analyzed を false にする
      divideArrayIntoChunks(hashes, sqliteMaxHostParameterCount - 1).map(
        (chunk) =>
          tx
            .update(accountEvmTxIndexRelationTable)
            .set({ analyzed: false })
            .where(inArray(accountEvmTxIndexRelationTable.hash, chunk)),
      ),
    (tx) =>
      // account.syncing を最新データに合わせて更新
      evmAccountRows.map(({ id }) =>
        tx
          .update(accountTable)
          .set({
            syncing: sql`(EXISTS (SELECT 1 FROM ${accountEvmTxIndexRelationTable} WHERE ${accountEvmTxIndexRelationTable.accountId} = ${id} AND ${accountEvmTxIndexRelationTable.analyzed} = ${false}))`,
          })
          .where(eq(accountTable.id, id)),
      ),
  ])

  return hashes.length
}
